import { apiWithPrefix as api } from '../utils/api';

/**
 * Employee Service
 * Handles all employee-related API calls
 */
export class EmployeeService {
  /**
   * Get all employees
   */
  static async getAll(filters = {}) {
    try {
      const response = await api.get('/employees', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employees');
    }
  }

  /**
   * Get employee by ID
   */
  static async getById(id) {
    try {
      const response = await api.get(`/employees/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employee');
    }
  }

  /**
   * Create new employee
   */
  static async create(employeeData) {
    try {
      const response = await api.post('/employees', employeeData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create employee');
    }
  }

  /**
   * Update employee
   */
  static async update(employeeData) {
    try {
      const response = await api.put('/employees', employeeData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update employee');
    }
  }

  /**
   * Delete employee
   */
  static async delete(email, options = {}) {
    try {
      const { hardDelete = false, leaving_date = null } = options;
      const response = await api.delete(`/employees/${email}`, {
        params: { hardDelete: hardDelete ? 'true' : 'false' },
        data: {
          leaving_date: hardDelete ? null : leaving_date
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete employee');
    }
  }

  /**
   * Get current user info
   */
  static async getCurrentUser() {
    try {
      const response = await api.get('/employee-info');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch user info');
    }
  }

  /**
   * Update current user info
   */
  static async updateCurrentUser(userData) {
    try {
      const response = await api.put('/employee-info', userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update user info');
    }
  }

  /**
   * Get employee emails (for dropdowns)
   */
  static async getEmployeeEmails() {
    try {
      const response = await api.get('/employee-email');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employee emails');
    }
  }

  /**
   * Search employees
   */
  static async search(query, filters = {}) {
    try {
      const params = { search: query, ...filters };
      const response = await api.get('/employees', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search employees');
    }
  }

  /**
   * Get employees by department
   */
  static async getByDepartment(department) {
    try {
      const response = await api.get('/employees', { 
        params: { department } 
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employees by department');
    }
  }

  /**
   * Get employees by role
   */
  static async getByRole(role) {
    try {
      const response = await api.get('/employees', { 
        params: { role } 
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employees by role');
    }
  }

  static normalizeEmployeeList(responseData) {
    const employeeData = responseData?.data || responseData || [];
    return employeeData.map((emp) => ({
      ...emp,
      phone: emp.phone_no,
      joinDate: emp.date_of_joining,
      leavingDate: emp.leaving_date || null,
      status: (emp.is_active ?? emp.isActive ?? true) ? 'active' : 'inactive'
    }));
  }

  /**
   * Upload profile picture (uses fetch, not axios — axios default JSON
   * Content-Type breaks multipart and multer sees no file).
   */
  static async uploadProfilePicture(file) {
    const base = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000').replace(
      /\/$/,
      ''
    );
    const formData = new FormData();
    formData.append('profilePicture', file);

    const headers = {};
    try {
      const userInfo = localStorage.getItem('user-info');
      if (userInfo) {
        const { accessToken, token } = JSON.parse(userInfo);
        const authToken = accessToken || token;
        if (authToken) headers.Authorization = `Bearer ${authToken}`;
      }
    } catch (_) {
      /* ignore */
    }

    const res = await fetch(`${base}/api/employee-info/profile-picture`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData
    });

    let data = {};
    try {
      data = await res.json();
    } catch (_) {
      /* non-JSON body */
    }

    if (!res.ok) {
      const err = new Error(data.message || `Upload failed (${res.status})`);
      err.response = { status: res.status, data };
      throw err;
    }
    return data;
  }
}

export default EmployeeService;
